import { animateMapChanges } from './map_animation.js';
import { FEET_PER_CELL } from './area_effect_geometry.js';
import { MovementDistances } from './movement_distance.js';

const MOVE_MS_PER_TILE = 250;
const MIN_MOVE_MS = 500;
const MAX_MOVE_MS = 3000;

// Animate only the visual offset. Node positions and grid anchors already hold
// the destination, so saves, undo and incoming snapshots never read a halfway move.
export const ShapeAnimationMethods = {
  _shapeVisualPositions() {
    return new Map(
      (this._shapes || []).map((shape) => {
        const node = shape.konvaShape;
        return [
          shape.id,
          {
            type: shape.type,
            x: node.x() - node.offsetX(),
            y: node.y() - node.offsetY(),
            targetX: node.x(),
            targetY: node.y(),
            motion: this._shapeMotions?.get(shape.id)
          }
        ];
      })
    );
  },

  _animateShapeMovement(positions) {
    this._finishShapeAnimations();
    this._movementDistances?.sync(this._shapes);
    if (!positions?.size || !animateMapChanges()) return;
    const gridSize = this._areaEffectGrid().size;
    if (!Number.isFinite(gridSize) || gridSize < 1) return;
    const startedAt = performance.now();
    this._shapeMotions = new Map();
    for (const shape of this._shapes) {
      const from = positions.get(shape.id);
      const node = shape.konvaShape;
      if (!from || from.type !== shape.type) continue;
      const x = node.x() - from.x;
      const y = node.y() - from.y;
      if (!x && !y) continue;
      // Keep the full curve across identical snapshots, not just its deadline:
      // restarting ease-in on every player poll would repeatedly slow the move.
      const sameDestination = from.targetX === node.x() && from.targetY === node.y();
      // Map-space cells keep timing independent of zoom and token size. Diagonal
      // steps count as one tile, matching the character's distance preview.
      const tiles = Math.max(Math.abs(x), Math.abs(y)) / gridSize;
      const duration = Math.min(MAX_MOVE_MS, Math.max(MIN_MOVE_MS, tiles * MOVE_MS_PER_TILE));
      const motion =
        sameDestination && from.motion
          ? { ...from.motion, shape }
          : { shape, x, y, feet: tiles * FEET_PER_CELL, startedAt, endsAt: startedAt + duration };
      if (motion.endsAt <= startedAt) continue;
      this._shapeMotions.set(shape.id, motion);
    }
    this._drawShapeAnimations(startedAt);
  },

  _drawShapeAnimations(now) {
    this._shapeAnimationFrame = null;
    if (!animateMapChanges()) return this._finishShapeAnimations();
    for (const [id, motion] of this._shapeMotions) {
      const progress = Math.max(0, Math.min(1, (now - motion.startedAt) / (motion.endsAt - motion.startedAt)));
      // Smoothstep: gently accelerate and decelerate, with no bounce/overshoot.
      const remaining = 1 - progress * progress * (3 - 2 * progress);
      const offset = { x: motion.x * remaining, y: motion.y * remaining };
      motion.shape.konvaShape.offset(offset);
      motion.shape.konvaLabels?.offset(offset);
      if (motion.shape.type === 'character') {
        this._movementDistances ||= new MovementDistances();
        this._movementDistances.show(motion.shape, motion, 1 - remaining);
      }
      if (!remaining) this._shapeMotions.delete(id);
    }
    this._syncEffectRotationHandle?.();
    this._shapesLayerAbove?.batchDraw();
    this._shapesLayerBelow?.batchDraw();
    if (this._shapeMotions.size)
      this._shapeAnimationFrame = requestAnimationFrame((time) => this._drawShapeAnimations(time));
  },

  _finishShapeAnimations() {
    const moving = this._shapeMotions?.size;
    if (this._shapeAnimationFrame != null) cancelAnimationFrame(this._shapeAnimationFrame);
    this._shapeAnimationFrame = null;
    for (const motion of this._shapeMotions?.values() || []) {
      const { shape } = motion;
      shape.konvaShape.offset({ x: 0, y: 0 });
      shape.konvaLabels?.offset({ x: 0, y: 0 });
      this._movementDistances?.show(shape, motion, 1);
    }
    this._shapeMotions?.clear();
    if (moving) this._syncEffectRotationHandle?.();
  }
};
